import { BasePerson } from 'shared/classes/Person';
import 'shared/ko/extenders';
import { add_acquisition_observables } from 'shared/classes/Acquisition';
import { PAYMENT_TERMS } from '../common/constants';
import { AdminArtist } from './Artist';
import { AdminHost } from './Host';
import { AdminEvent } from './Event';

function normalizeContactSettings(contactSettings) {
  return {
    whatsapp: {
      allowed: contactSettings?.whatsapp?.allowed ?? true,
    },
    call: {
      allowed: contactSettings?.call?.allowed ?? true,
    },
  }
}

export class AdminPerson extends BasePerson {
  constructor(parameters) {
    super(parameters)

    this.type = 'AdminPerson'

    // Upgrade nested instances to Admin variants.
    // Conditional on data: empty case keeps base placeholders so we don't
    // recurse into AdminArtist/AdminHost (which always create a nested AdminPerson).
    if (parameters.artist) {
      const artistData = { ...parameters.artist, person: undefined }
      this.artist(new AdminArtist(artistData))
    }
    if (parameters.host) {
      const hostData = { ...parameters.host, person: undefined }
      this.host(new AdminHost(hostData))
    }

    this.events = ko.observableArray(
      (parameters.events ?? []).map(e =>
        e instanceof AdminEvent ? e : new AdminEvent({ ...e, rootvm: this.rootvm })
      )
    )

    // Email & phone editing are admin-only; trackChange gives isDirty/reset/commit.
    this.email_address.extend({ trackChange: true })
    this.edit_email = ko.observable(false)
    this.phone.extend({ trackChange: true })
    this.edit_phone = ko.observable(false)

    const contactSettings = normalizeContactSettings(parameters.contact_settings)
    this.contact_settings = {
      whatsapp: {
        allowed: ko.observable(contactSettings.whatsapp.allowed).extend({ trackChange: true }),
      },
      call: {
        allowed: ko.observable(contactSettings.call.allowed).extend({ trackChange: true }),
      },
    }
    this.payment_term_options = ko.observableArray(PAYMENT_TERMS)

    // First touch channel, so the members list shows the same referrer icon
    // as the invite list.
    add_acquisition_observables(this, parameters.acquisition)
  }

  getType() {
      return this.type
  }

  update_person_details(d) {
    super.update_person_details(d)
    this.email_address.commit()
    this.phone.commit()
    if (d) {
      const contactSettings = normalizeContactSettings(d.contact_settings)
      this.contact_settings.whatsapp.allowed(contactSettings.whatsapp.allowed)
      this.contact_settings.whatsapp.allowed.commit()
      this.contact_settings.call.allowed(contactSettings.call.allowed)
      this.contact_settings.call.allowed.commit()
    }
  }

  copyEmail() {

    // Copy the text inside the text field
    navigator.clipboard.writeText(this.email_address());

    notyf.success('Copied email address to clipboard')
  }

  async update_email() {
    this.rootvm.is_busy(true)
    try {
      const response = await fetch("/api/person/change-email", {
        method: "POST",
        headers: { "Content-Type": "application/json; charset=utf-8" },
        body: ko.toJSON({
          new_email: this.email_address().trim(),
          person_uuid: this.person_uuid(),
        }),
      })
      const data = await response.json()
      if (response.ok && data.success) {
        this.email_address(data.email_address)
        this.email_address.commit()
        this.edit_email(false)
        notyf.success(data.message)
      } else {
        notyf.error(data.message)
      }
    } finally {
      this.rootvm.is_busy(false)
    }
  }

  async update_phone() {
    this.rootvm.is_busy(true)
    try {
      const response = await fetch("/api/admin/person/change-phone", {
        method: "POST",
        headers: { "Content-Type": "application/json; charset=utf-8" },
        body: ko.toJSON({
          phone: (this.phone() ?? "").trim(),
          person_uuid: this.person_uuid(),
        }),
      })
      const data = await response.json()
      if (response.ok && data.success) {
        this.phone(data.phone)
        this.phone.commit()
        this.edit_phone(false)
        notyf.success(data.message)
      } else {
        notyf.error(data.message)
      }
    } finally {
      this.rootvm.is_busy(false)
    }
  }

  async set_contact_setting(nestedPath) {
    const [contactType, settingName] = nestedPath.split(".")
    const setting = this.contact_settings?.[contactType]?.[settingName]

    if (!setting) {
      throw new Error(`Unknown contact setting path: ${nestedPath}`)
    }

    const response = await fetch(
      `${scvm.web_host}/api/admin/people/contact-settings`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
        },
        body: ko.toJSON({
          person_uuid: this.person_uuid(),
          contact_type: contactType,
          setting_name: settingName,
          value: setting(),
        }),
      }
    )

    const data = await response.json()

    if (response.ok && data.success) {
      setting.commit()
      notyf.success(data.message)
      return data
    }

    notyf.error(data.message)
    throw new Error(data.message)
  }

  async get_login_link(path, perspective) {
    try {
      const response = await fetch(
        `${scvm.web_host}/api/admin/get-login-user-link?` +
        new URLSearchParams({ person_uuid: this.person_uuid() }),
      )
      const result = await response.json()

      let login_link = result.login_link
      if (path) {
        const param_index = login_link.indexOf('?')
        login_link = login_link.slice(0, param_index) + path + login_link.slice(param_index)
      }

      if (perspective === 'artist') {
        this.login_link_artist(login_link)
      } else if (perspective === 'host') {
        this.login_link_host(login_link)
      } else {
        this.login_link(login_link)
      }
    } catch (err) {
      console.error(err.message)
    }
  }

  async sign_in_with_key(key, person_uuid) {
    this.message(null)
    this.rootvm.syslog('Signing in')
    this.rootvm.is_busy(true)

    try {
      const response = await fetch('/api/admin/login/key', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: ko.toJSON({ key, person_uuid }),
      })

      const data = await response.json()

      if (data.success) {
        this.rootvm.webapp_session().update_self(data)
        this.raw_password(null)
      } else {
        this.message(data.message)
      }
    } finally {
      this.rootvm.syslog('Finished sign in')
      this.rootvm.is_busy(false)
    }
  }

  async admin_sign_in() {
    this.message(null)
    this.rootvm.syslog('Signing in')
    this.rootvm.is_busy(true)

    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: ko.toJSON({
          email_address: this.email_address(),
          password: this.raw_password(),
        }),
      })

      const data = await response.json()

      if (data.success) {
        this.rootvm.webapp_session().update_self(data)
        this.raw_password(null)
      } else {
        this.message(data.message)
        this.show_login_error(true)
      }
    } catch (err) {
      this.message('Login failed')
      this.show_login_error(true)
    } finally {
      this.rootvm.syslog('Finished sign in')
      this.rootvm.is_busy(false)
    }
  }

  async close_all_open_events() {
    const response = await fetch('/api/admin/people/close-all-events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: ko.toJSON({ person_uuid: this.person_uuid() }),
    })
    const data = await response.json()
    if (!data.success) throw new Error(data.message)
    return data
  }

  async lookupDetails() {
    const url = scvm.web_host + "/api/admin/person";
    const params = new URLSearchParams({
      person_uuid: this.person_uuid(),
    });
    const request = new Request(url + "?" + params.toString(), {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    });
    const response = await fetch(request);
    const data = await response.json();
    if (data.success) {
      // console.log("Fetched person details", data.person);
      self = new AdminPerson(data.person);
      return self;
    } else {
      console.error(data.pgcode, data.pgerror);
      throw new Error(data.message);
    }
  };
}
